import { useMemo, useState } from 'react'
import { TextInput } from '@forwardflow/ui-v2'
import { SearchMagnifyingGlassIcon } from '@forwardflow/icons'
import { useCatalog } from '../../queries/campaign-v2.queries'
import { ICatalogItem, TContentKind } from '../../interfaces/campaign-v2.types'
import {
   AddRow,
   EmptyNote,
   ListCard,
   ListRow,
   Loading,
   Pill,
   TextButton,
} from '../shared'

/**
 * Choose from the library: shared templates and the organization's own
 * items for this category. "Use" binds it as is; "Customize" opens the
 * editor on a copy; "Write your own" starts blank. Revision ids and
 * selection policy never appear (handoff, section 2).
 */
export const ContentPicker = ({
   kind,
   categoryId,
   direction,
   callerType = 'ai',
   exclude,
   onUse,
   onCustomize,
   onPreview,
   onWrite,
   onClose,
   busy,
   title,
}: {
   kind: TContentKind
   categoryId: number
   /** Phone only: filter by call direction. */
   direction?: 'outbound' | 'inbound'
   callerType?: 'ai' | 'human'
   /** Content ids already bound in this slot. */
   exclude: string[]
   onUse: (item: ICatalogItem) => void
   onCustomize: (item: ICatalogItem) => void
   onPreview?: (item: ICatalogItem) => void
   onWrite: () => void
   onClose: () => void
   busy?: boolean
   title: string
}) => {
   const { data, isLoading, isValidating, error, mutate } = useCatalog(
      kind,
      categoryId
   )
   const [q, setQ] = useState('')

   const items = useMemo(
      () =>
         (data || [])
            .filter((i) => !exclude.includes(String(i.contentId)))
            .filter(
               (i) =>
                  !direction ||
                  !i.callDirections?.length ||
                  i.callDirections.includes(direction)
            )
            .filter((i) =>
               kind === 'phone'
                  ? !i.callerTypes?.length || i.callerTypes.includes(callerType)
                  : true
            )
            .filter(
               (i) =>
                  !q.trim() ||
                  i.name.toLowerCase().includes(q.trim().toLowerCase())
            ),
      [data, exclude, direction, callerType, kind, q]
   )

   return (
      <ListCard
         title={title}
         hint="Shared templates are used as is. Customize one to change its wording for this campaign only."
         right={
            <TextButton onClick={onClose} tone="muted" testId="picker-close">
               Close
            </TextButton>
         }
         testId={`picker-${kind}`}
      >
         <AddRow
            label={
               kind === 'strategy'
                  ? 'Create an outbound content group'
                  : 'Create new content'
            }
            onClick={onWrite}
            testId="picker-write"
            disabled={busy}
         />
         <div className="px-4 py-3">
            <TextInput
               size="sm"
               leftSection={<SearchMagnifyingGlassIcon className="w-4 h-4" />}
               shouldLeftSectionBeFocused
               placeholder="Search the library"
               value={q}
               onChange={(e) => setQ(e.currentTarget.value)}
               data-testid="picker-search"
            />
         </div>
         {isLoading && !data && !error && (
            <Loading label="Loading the library" />
         )}
         {error && (
            <EmptyNote>
               <span role="alert">Could not load the library.</span>{' '}
               <TextButton
                  onClick={() => void mutate().catch(() => undefined)}
                  disabled={isValidating}
                  testId="picker-retry"
               >
                  {isValidating ? 'Retrying…' : 'Try again'}
               </TextButton>{' '}
               or create your own content above.
            </EmptyNote>
         )}
         {!isLoading && !error && !items.length && (
            <EmptyNote>
               {q.trim() ? (
                  <>
                     No content matches “{q.trim()}”.{' '}
                     <TextButton
                        onClick={() => setQ('')}
                        testId="picker-clear-search"
                     >
                        Clear search
                     </TextButton>
                  </>
               ) : (
                  'Nothing here for this category yet. Create your own content above.'
               )}
            </EmptyNote>
         )}
         {!error && items.map((item) => (
            <ListRow
               key={item.contentId}
               title={item.name}
               sub={describe(item)}
               right={
                  <>
                     <Pill tone={item.shared ? 'muted' : 'purple'}>
                        {item.shared ? 'template' : 'yours'}
                     </Pill>
                     {onPreview && (
                        <TextButton
                           onClick={() => onPreview(item)}
                           tone="muted"
                           disabled={busy}
                           testId={`view-${item.contentId}`}
                        >
                           View
                        </TextButton>
                     )}
                     <TextButton
                        onClick={() => onCustomize(item)}
                        tone="muted"
                        disabled={busy}
                        testId={`customize-${item.contentId}`}
                     >
                        Customize
                     </TextButton>
                     <TextButton
                        onClick={() => onUse(item)}
                        disabled={busy}
                        testId={`use-${item.contentId}`}
                     >
                        Use
                     </TextButton>
                  </>
               }
               testId={`item-${item.contentId}`}
            />
         ))}
      </ListCard>
   )
}

const describe = (i: ICatalogItem) => {
   if (i.kind === 'strategy' && i.counts) {
      const parts = [
         i.counts.sms
            ? `${i.counts.sms} text${i.counts.sms === 1 ? '' : 's'}`
            : '',
         i.counts.email
            ? `${i.counts.email} email${i.counts.email === 1 ? '' : 's'}`
            : '',
         i.counts.voicemail
            ? `${i.counts.voicemail} voicemail${i.counts.voicemail === 1 ? '' : 's'}`
            : '',
      ].filter(Boolean)
      return parts.join(' · ') || 'Empty group'
   }
   if (i.kind === 'phone') {
      const who = i.leadTypes?.includes('unknown')
         ? i.leadTypes.includes('known')
            ? 'known and unknown callers'
            : 'unknown callers only'
         : 'known leads'
      return `${i.callDirections?.includes('inbound') ? 'Incoming' : 'Outgoing'} · ${who}`
   }
   return i.description || undefined
}
